import React, { useState, useEffect, useCallback, useMemo } from "react";

import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  OverlayTrigger,
  Popover,
  ProgressBar,
  Stack,
  Pagination,
  Spinner,
  Alert,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";
import {
  getSevkiyatList,
  updateSevkiyatList,
  addSevkiyatList,
  deleteSevkiyatList,
  bulkSevkiyatList,
} from "../../../api/sevkiyat/sevkiyatList";
import ShipmentLiveCameras from "./ShipmentLiveCameras";

/* ================= SÜRE HESAPLAMA ================= */
const calculateDuration = (giris, cikis) => {
  if (!giris || !cikis) return "-";

  const [gSaat, gDakika] = giris.split(":").map(Number);
  const [cSaat, cDakika] = cikis.split(":").map(Number);

  if (
    Number.isNaN(gSaat) ||
    Number.isNaN(gDakika) ||
    Number.isNaN(cSaat) ||
    Number.isNaN(cDakika)
  ) {
    return "-";
  }

  let totalDakikaGiris = gSaat * 60 + gDakika;
  let totalDakikaCikis = cSaat * 60 + cDakika;

  if (totalDakikaCikis < totalDakikaGiris) {
    totalDakikaCikis += 24 * 60;
  }

  const farkDakika = totalDakikaCikis - totalDakikaGiris;
  const saat = Math.floor(farkDakika / 60);
  const dakika = farkDakika % 60;

  return `${saat.toString().padStart(2, "0")}:${dakika
    .toString()
    .padStart(2, "0")}`;
};

/* ================= EXCEL PASTE HELPERS ================= */
const normalizeTR = (value = "") => {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ")
    .replace(/İ/g, "I")
    .replace(/I/g, "I")
    .replace(/Ğ/g, "G")
    .replace(/Ü/g, "U")
    .replace(/Ş/g, "S")
    .replace(/Ö/g, "O")
    .replace(/Ç/g, "C");
};

const parseExcelNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;

  const raw = String(value).trim();

  if (!raw) return 0;

  const cleaned = raw.replace(/\./g, "").replace(",", ".");

  return Number(cleaned) || 0;
};

const parseExcelDateToInputDate = (value) => {
  if (!value) return "";

  const raw = String(value).trim();

  const match = raw.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/);

  if (!match) return raw;

  const day = match[1].padStart(2, "0");
  const month = match[2].padStart(2, "0");
  let year = match[3];

  if (year.length === 2) {
    year = `20${year}`;
  }

  return `${year}-${month}-${day}`;
};

const normalizeTime = (value) => {
  if (!value) return "";

  const raw = String(value).trim();

  const match = raw.match(/^(\d{1,2}):(\d{1,2})$/);

  if (!match) return raw;

  const hour = match[1].padStart(2, "0");
  const minute = match[2].padStart(2, "0");

  return `${hour}:${minute}`;
};

const shipmentHeaderMap = {
  SIRA: "sira",
  TARIH: "tarih",
  "IRSALIYE NO": "irsaliyeNo",
  PLAKA: "plaka",
  "SEVK YERI": "sevkYeri",
  MUSTERI: "musteri",
  CINSI: "cinsi",
  "SOGUK/GALVANIZ": "tip",
  ADET: "adet",

  "FIRMA GIRIS KG": "firmaGirisKg",

  "GOKTAS CIKIS KG.": "goktasCikisKg",
  "GOKTAS CIKIS KG": "goktasCikisKg",

  "SATIS TURU": "satisTur",

  "MOS CIKIS KG.": "mosCikisKg",
  "MOS CIKIS KG": "mosCikisKg",

  "FARK KG": "farkKg",
  "CIKIS FARK KG": "farkKg",

  DURUM: "durum",
  NAKLIYE: "nakliye",
  "FATURA NO": "faturaNo",
  "ARAC GIRIS SAAT": "aracGirisSaat",
  "ARAC CIKIS SAAT": "aracCikisSaat",
  "SURE FARK": "sureFark",
  "KONTEYNER NO": "konteynerNo",
};

const defaultShipmentColumns = [
  "sira",
  "tarih",
  "irsaliyeNo",
  "plaka",
  "sevkYeri",
  "musteri",
  "cinsi",
  "tip",
  "adet",
  "firmaGirisKg",
  "goktasCikisKg",
  "satisTur",
  "mosCikisKg",
  "farkKg",
  "durum",
  "nakliye",
  "faturaNo",
  "aracGirisSaat",
  "aracCikisSaat",
  "sureFark",
  "konteynerNo",
];

const validatePasteRow = (row) => {
  const errors = [];

  if (!row.tarih) errors.push("Tarih eksik");
  if (!row.plaka) errors.push("Plaka eksik");
  if (!row.sevkYeri) errors.push("Sevk yeri eksik");
  if (!row.musteri) errors.push("Müşteri eksik");
  if (!row.adet) errors.push("Adet eksik");
  if (!row.goktasCikisKg) errors.push("Göktaş çıkış kg eksik");
  if (!row.durum) errors.push("Durum eksik");

  return errors;
};

const parseShipmentPasteText = (rawText) => {
  const lines = String(rawText || "")
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim());

  if (!lines.length) return [];

  const tableRows = lines.map((line) =>
    line.split("\t").map((cell) => String(cell || "").trim())
  );

  const firstRowNormalized = tableRows[0].map(normalizeTR);

  const hasHeader = firstRowNormalized.some(
    (header) => shipmentHeaderMap[header]
  );

  const columns = hasHeader
    ? firstRowNormalized.map((header) => shipmentHeaderMap[header] || null)
    : defaultShipmentColumns;

  const dataRows = hasHeader ? tableRows.slice(1) : tableRows;

  return dataRows.map((cols, index) => {
    const row = {
      _rowNo: index + 1,
      _selected: true,
      sira: "",
      tarih: "",
      irsaliyeNo: "",
      plaka: "",
      sevkYeri: "",
      musteri: "",
      cinsi: "",
      tip: "",
      adet: 0,
      firmaGirisKg: 0,
      goktasCikisKg: 0,
      satisTur: "",
      mosCikisKg: 0,
      farkKg: 0,
      durum: "",
      nakliye: "",
      faturaNo: "",
      aracGirisSaat: "",
      aracCikisSaat: "",
      sureFark: "",
      konteynerNo: "",
    };

    columns.forEach((key, colIndex) => {
      if (!key) return;
      row[key] = cols[colIndex] || "";
    });

    row.sira = row.sira ? Number(row.sira) || "" : "";
    row.tarih = parseExcelDateToInputDate(row.tarih);

    row.irsaliyeNo = String(row.irsaliyeNo || "").trim();

    row.plaka = String(row.plaka || "")
      .trim()
      .toUpperCase();

    row.sevkYeri = String(row.sevkYeri || "").trim();
    row.musteri = String(row.musteri || "").trim();
    row.cinsi = String(row.cinsi || "").trim();

    row.tip = String(row.tip || "")
      .trim()
      .toUpperCase();

    row.satisTur = String(row.satisTur || "").trim();

    row.durum = String(row.durum || "")
      .trim()
      .toUpperCase();

    row.nakliye = String(row.nakliye || "").trim();
    row.faturaNo = String(row.faturaNo || "").trim();
    row.konteynerNo = String(row.konteynerNo || "").trim();

    row.adet = parseExcelNumber(row.adet);
    row.firmaGirisKg = parseExcelNumber(row.firmaGirisKg);
    row.goktasCikisKg = parseExcelNumber(row.goktasCikisKg);
    row.mosCikisKg = parseExcelNumber(row.mosCikisKg);

    const hasExcelFarkKg =
      row.farkKg !== "" && row.farkKg !== null && row.farkKg !== undefined;

    row.farkKg = hasExcelFarkKg
      ? parseExcelNumber(row.farkKg)
      : row.mosCikisKg
        ? Number(row.goktasCikisKg || 0) - Number(row.mosCikisKg || 0)
        : 0;

    row.aracGirisSaat = normalizeTime(row.aracGirisSaat);
    row.aracCikisSaat = normalizeTime(row.aracCikisSaat);
    row.sureFark =
      row.sureFark || calculateDuration(row.aracGirisSaat, row.aracCikisSaat);

    row._errors = validatePasteRow(row);

    return row;
  });
};

/* ================= ICON ================= */
const FilterIcon = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    fill={active ? "#0d6efd" : "#adb5bd"}
    className="ms-1"
    viewBox="0 0 16 16"
    style={{ cursor: "pointer" }}
  >
    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z" />
  </svg>
);

/* ================= EXCEL FILTER COMPONENT ================= */
const ExcelColumnFilter = ({
  columnKey,
  title,
  options = [],
  activeFilters,
  onApply,
}) => {
  const [tempSelected, setTempSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];

  const currentFilterValue = activeFilters[columnKey];

  const isFilterActive =
    Array.isArray(currentFilterValue) && currentFilterValue.length > 0;

  const handleEnter = () => {
    setSearchTerm("");

    if (isFilterActive) {
      setTempSelected(currentFilterValue);
    } else {
      setTempSelected(safeOptions);
    }
  };

  const handleCheckboxChange = (val) => {
    if (tempSelected.includes(val)) {
      setTempSelected(tempSelected.filter((item) => item !== val));
    } else {
      setTempSelected([...tempSelected, val]);
    }
  };

  const handleSelectAll = (checked) => {
    const visibleOptions = safeOptions.filter((opt) =>
      opt.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (checked) {
      setTempSelected([...new Set([...tempSelected, ...visibleOptions])]);
    } else {
      if (searchTerm) {
        setTempSelected(
          tempSelected.filter((item) => !visibleOptions.includes(item))
        );
      } else {
        setTempSelected([]);
      }
    }
  };

  const handleApplyClick = () => {
    let nextValue;

    if (
      tempSelected.length === safeOptions.length ||
      tempSelected.length === 0
    ) {
      nextValue = undefined;
    } else {
      nextValue = tempSelected;
    }

    onApply(columnKey, nextValue);
    document.body.click();
  };

  const handleClearClick = () => {
    onApply(columnKey, undefined);
    document.body.click();
  };

  const filteredOptions = safeOptions.filter((opt) =>
    opt.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  const popover = (
    <Popover
      id={`popover-${columnKey}`}
      className="shadow"
      style={{ maxWidth: "250px" }}
    >
      <Popover.Body className="p-2">
        <Form.Control
          size="sm"
          placeholder="Ara..."
          className="mb-2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />

        <div className="border-bottom pb-2 mb-2">
          <Form.Check
            type="checkbox"
            id={`select-all-${columnKey}`}
            label={<span className="fw-bold small">Tümünü Seç</span>}
            checked={
              filteredOptions.length > 0 &&
              filteredOptions.every((opt) => tempSelected.includes(opt))
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </div>

        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((val, idx) => (
              <Form.Check
                key={idx}
                type="checkbox"
                id={`chk-${columnKey}-${idx}`}
                label={<span style={{ fontSize: "12px" }}>{val}</span>}
                checked={tempSelected.includes(val)}
                onChange={() => handleCheckboxChange(val)}
                className="mb-1"
              />
            ))
          ) : (
            <div className="text-muted small text-center">Sonuç yok</div>
          )}
        </div>

        <div className="d-flex justify-content-between gap-2 mt-3 border-top pt-2">
          <Button
            variant="link"
            size="sm"
            className="text-decoration-none text-danger p-0"
            style={{ fontSize: "12px" }}
            onClick={handleClearClick}
          >
            Temizle
          </Button>

          <Button
            variant="primary"
            size="sm"
            style={{ fontSize: "11px" }}
            onClick={handleApplyClick}
          >
            Tamam
          </Button>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom"
      rootClose={true}
      overlay={popover}
      onEnter={handleEnter}
    >
      <span
        className="d-flex align-items-center justify-content-between gap-1"
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {title}
        <FilterIcon active={isFilterActive} />
      </span>
    </OverlayTrigger>
  );
};

/* ================= MAIN COMPONENT ================= */
function SevkiyatListe() {
  const { user, permissions } = useAuth();

  const [rows, setRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [showModal, setShowModal] = useState(false);

  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [isExporting, setIsExporting] = useState(false);

  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteRows, setPasteRows] = useState([]);
  const [isPasteSaving, setIsPasteSaving] = useState(false);

  const [columnFilters, setColumnFilters] = useState({});
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fmtNum = (num) => {
    const value = Number(num);

    if (!value) return "";

    return value.toLocaleString("tr-TR");
  };

  const formatDateTR = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) return dateStr;

    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const getFormDate = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  };

  const fetchData = useCallback(
    async (overrideFilters = null, overridePage = null) => {
      setIsLoading(true);

      try {
        const params = {
          page: overridePage || currentPage,
          limit: 20,
          startDate: dateRange.start,
          endDate: dateRange.end,
          ...(overrideFilters || columnFilters),
        };

        const res = await getSevkiyatList(params);
        const payload = res.data ? res.data : res;

        if (payload && payload.success) {
          setRows(payload.data || []);
          setFilterOptions(payload.filters || {});

          if (payload.pagination) {
            setTotalPages(payload.pagination.totalPage || 1);
            setTotalRecords(payload.pagination.total || 0);
          }
        }
      } catch (error) {
        console.error("Hata:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, dateRange.start, dateRange.end, columnFilters]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterUpdate = (key, value) => {
    const newFilters = { ...columnFilters, [key]: value };

    if (!value) delete newFilters[key];

    setColumnFilters(newFilters);
    setCurrentPage(1);
    fetchData(newFilters, 1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const stats = useMemo(() => {
    const toplamKg = rows.reduce(
      (acc, r) => acc + (Number(r.goktasCikisKg) || 0),
      0
    );

    const toplamMosKg = rows.reduce(
      (acc, r) => acc + (Number(r.mosCikisKg) || 0),
      0
    );

    const toplamFarkKg = toplamKg - toplamMosKg;

    const toplamAdet = rows.reduce((acc, r) => acc + (Number(r.adet) || 0), 0);

    const musteriMap = {};

    rows.forEach((r) => {
      if (!musteriMap[r.musteri]) musteriMap[r.musteri] = 0;
      musteriMap[r.musteri] += Number(r.goktasCikisKg) || 0;
    });

    let maxTonajRecord = null;
    let maxVal = 0;

    rows.forEach((r) => {
      const val = Number(r.goktasCikisKg) || 0;

      if (val > maxVal) {
        maxVal = val;
        maxTonajRecord = r;
      }
    });

    const topMusteriler = Object.entries(musteriMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return {
      toplamKg,
      toplamMosKg,
      toplamFarkKg,
      toplamAdet,
      count: rows.length,
      topMusteriler,
      maxTonajRecord,
    };
  }, [rows]);

  const handleExportExcel = async () => {
    setIsExporting(true);

    try {
      const params = {
        page: 1,
        limit: 10000,
        startDate: dateRange.start,
        endDate: dateRange.end,
        ...columnFilters,
      };

      const res = await getSevkiyatList(params);
      const payload = res.data ? res.data : res;

      const allData = payload.data || [];

      if (allData.length === 0) {
        alert("Dışa aktarılacak veri bulunamadı.");
        setIsExporting(false);
        return;
      }

      const dataToExport = allData.map((r) => {
        const goktasKg = Number(r.goktasCikisKg) || 0;
        const mosKg = Number(r.mosCikisKg) || 0;
        const farkKg =
          r.farkKg !== undefined && r.farkKg !== null
            ? Number(r.farkKg) || 0
            : goktasKg - mosKg;

        return {
          Sıra: r.sira || "",
          Tarih: formatDateTR(r.tarih),
          "İrsaliye No": r.irsaliyeNo,
          Plaka: r.plaka,
          "Sevk Yeri": r.sevkYeri,
          Müşteri: r.musteri,
          Cinsi: r.cinsi,
          Tip: r.tip,
          Adet: Number(r.adet) || 0,
          "Firma Giriş KG": Number(r.firmaGirisKg) || 0,
          "Göktaş Çıkış KG": goktasKg,
          "Satış Türü": r.satisTur || "",
          "Mos Çıkış KG": mosKg,
          "Fark KG": farkKg,
          Durum: r.durum,
          Nakliye: r.nakliye,
          "Fatura No": r.faturaNo,
          "Araç Giriş": r.aracGirisSaat,
          "Araç Çıkış": r.aracCikisSaat,
          "Süre Fark":
            r.sureFark || calculateDuration(r.aracGirisSaat, r.aracCikisSaat),
          "Konteyner No": r.konteynerNo,
        };
      });

      const totalAdet = dataToExport.reduce((acc, curr) => acc + curr.Adet, 0);

      const totalFirmaGirisKg = dataToExport.reduce(
        (acc, curr) => acc + curr["Firma Giriş KG"],
        0
      );

      const totalKg = dataToExport.reduce(
        (acc, curr) => acc + curr["Göktaş Çıkış KG"],
        0
      );

      const totalMosKg = dataToExport.reduce(
        (acc, curr) => acc + curr["Mos Çıkış KG"],
        0
      );

      const totalFarkKg = dataToExport.reduce(
        (acc, curr) => acc + curr["Fark KG"],
        0
      );

      const totalRow = {
        Sıra: "",
        Tarih: "GENEL TOPLAM",
        "İrsaliye No": "",
        Plaka: "",
        "Sevk Yeri": "",
        Müşteri: "",
        Cinsi: "",
        Tip: "",
        Adet: totalAdet,
        "Firma Giriş KG": totalFirmaGirisKg,
        "Göktaş Çıkış KG": totalKg,
        "Satış Türü": "",
        "Mos Çıkış KG": totalMosKg,
        "Fark KG": totalFarkKg,
        Durum: "",
        Nakliye: "",
        "Fatura No": "",
        "Araç Giriş": "",
        "Araç Çıkış": "",
        "Süre Fark": "",
        "Konteyner No": "",
      };

      dataToExport.push(totalRow);

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Tüm Sevkiyatlar");

      const today = new Date().toISOString().slice(0, 10);

      XLSX.writeFile(wb, `Sevkiyat_Listesi_Tam_${today}.xlsx`);
    } catch (error) {
      console.error("Excel Export Hatası:", error);
      alert("Excel oluşturulurken bir hata oluştu.");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePreparePasteRows = (textValue = pasteText) => {
    const parsed = parseShipmentPasteText(textValue);

    if (!parsed.length) {
      setPasteRows([]);
      alert("Yapıştırılan veri okunamadı.");
      return;
    }

    setPasteRows(parsed);
  };

  const handleClearPasteModal = () => {
    setPasteText("");
    setPasteRows([]);
  };

  const handleTogglePasteRow = (rowNo) => {
    setPasteRows((prev) =>
      prev.map((row) =>
        row._rowNo === rowNo ? { ...row, _selected: !row._selected } : row
      )
    );
  };

  const handleToggleAllPasteRows = (checked) => {
    setPasteRows((prev) => prev.map((row) => ({ ...row, _selected: checked })));
  };

  const handleSavePasteRows = async () => {
    if (!user?.id) {
      return alert("Kullanıcı oturumu bulunamadı! Lütfen tekrar giriş yapın.");
    }

    const selectedRows = pasteRows.filter((row) => row._selected);

    if (!selectedRows.length) {
      return alert("Aktarılacak seçili satır bulunamadı.");
    }

    const invalidRows = selectedRows.filter((row) => row._errors?.length);

    if (invalidRows.length > 0) {
      return alert(
        `${invalidRows.length} seçili satırda eksik/hatalı alan var. Lütfen kırmızı satırları kontrol edin.`
      );
    }

    const confirmSave = window.confirm(
      `${selectedRows.length} adet sevkiyat kaydı toplu olarak sisteme eklenecek. Onaylıyor musunuz?`
    );

    if (!confirmSave) return;

    setIsPasteSaving(true);

    try {
      const payload = {
        data: selectedRows.map((row) => ({
          userId: user.id,
          sira: row.sira || "",
          plaka: row.plaka || "",
          tarih: row.tarih || "",
          irsaliyeNo: row.irsaliyeNo || "",
          sevkYeri: row.sevkYeri || "",
          musteri: row.musteri || "",
          cinsi: row.cinsi || "",
          tip: row.tip || "",
          satisTur: row.satisTur || "",
          adet: Number(row.adet) || 0,
          firmaGirisKg: Number(row.firmaGirisKg) || 0,
          goktasCikisKg: Number(row.goktasCikisKg) || 0,
          mosCikisKg: Number(row.mosCikisKg) || 0,
          farkKg: Number(row.farkKg) || 0,
          durum: row.durum || "",
          nakliye: row.nakliye || "",
          faturaNo: row.faturaNo || "",
          aracGirisSaat: row.aracGirisSaat || "",
          aracCikisSaat: row.aracCikisSaat || "",
          sureFark: row.sureFark || "",
          konteynerNo: row.konteynerNo || "",
        })),
      };

      const res = await bulkSevkiyatList(payload);
      const response = res?.data || res;

      const summary = response?.summary || {};

      alert(
        `Toplu aktarım tamamlandı.\n` +
          `Gönderilen: ${summary.incomingCount ?? selectedRows.length}\n` +
          `Eklenen: ${summary.insertedCount ?? 0}\n` +
          `Zaten sistemde olan: ${summary.alreadyExistsCount ?? 0}\n` +
          `Excel içinde tekrar eden: ${
            summary.duplicateInRequestCount ?? 0
          }\n` +
          `Boş/geçersiz kayıt: ${summary.emptyKeyCount ?? 0}`
      );

      await fetchData();

      setShowPasteModal(false);
      handleClearPasteModal();
    } catch (error) {
      console.error("Bulk sevkiyat kayıt hatası:", error);

      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Toplu kayıt sırasında hata oluştu.";

      alert("Hata: " + errMsg);
    } finally {
      setIsPasteSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.plaka) return alert("Lütfen Plaka giriniz.");

    if (!user?.id) {
      return alert("Kullanıcı oturumu bulunamadı! Lütfen tekrar giriş yapın.");
    }

    setIsLoading(true);

    try {
      const goktasKg = form.goktasCikisKg ? Number(form.goktasCikisKg) : 0;
      const mosKg = form.mosCikisKg ? Number(form.mosCikisKg) : 0;

      const payload = {
        userId: user.id,
        sira: form.sira || "",
        plaka: form.plaka,
        tarih: form.tarih || "",
        irsaliyeNo: form.irsaliyeNo || "",
        sevkYeri: form.sevkYeri || "",
        musteri: form.musteri || "",
        cinsi: form.cinsi || "",
        tip: form.tip || "",
        satisTur: form.satisTur || "",
        durum: form.durum || "",
        nakliye: form.nakliye || "",
        faturaNo: form.faturaNo || "",
        konteynerNo: form.konteynerNo || "",
        aracGirisSaat: form.aracGirisSaat || "",
        aracCikisSaat: form.aracCikisSaat || "",
        sureFark:
          form.sureFark ||
          calculateDuration(form.aracGirisSaat, form.aracCikisSaat),
        adet: form.adet ? Number(form.adet) : 0,
        firmaGirisKg: form.firmaGirisKg ? Number(form.firmaGirisKg) : 0,
        goktasCikisKg: goktasKg,
        mosCikisKg: mosKg,
        farkKg:
          form.farkKg !== undefined &&
          form.farkKg !== null &&
          form.farkKg !== ""
            ? Number(form.farkKg) || 0
            : goktasKg - mosKg,
      };

      if (editRow) {
        payload.updatedId = editRow._id;
        await updateSevkiyatList(payload);
        alert("Kayıt başarıyla güncellendi.");
      } else {
        await addSevkiyatList(payload);
        alert("Yeni kayıt başarıyla eklendi.");
      }

      await fetchData();

      setShowModal(false);
      setEditRow(null);
      setForm({});
    } catch (error) {
      console.error("Kayıt Hatası:", error);

      const errMsg =
        error.response?.data?.error || error.message || "İşlem başarısız.";

      alert("Hata: " + errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRow) return;

    if (!user?.id) {
      return alert("Kullanıcı oturumu bulunamadı!");
    }

    setIsLoading(true);

    try {
      await deleteSevkiyatList({
        userId: user.id,
        deletedId: deleteRow._id,
      });

      alert("Kayıt başarıyla silindi.");

      setDeleteRow(null);
      fetchData();
    } catch (error) {
      console.error("Silme Hatası:", error);

      const errMsg =
        error.response?.data?.error ||
        error.message ||
        "Silme işlemi başarısız.";

      alert("Hata: " + errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const renderBadge = (status) => {
    const map = {
      SEVK: "success",
      "FASON DİLME": "info",
      "Y.İÇİ SATIN ALMA": "warning",
      İADE: "danger",
      İHRACAT: "primary",
      İTHALAT: "dark",
      BEKLEMEDE: "secondary",
    };

    return (
      <Badge
        bg={map[status] || "secondary"}
        style={{ fontSize: "10px", fontWeight: "500", padding: "4px 6px" }}
      >
        {status}
      </Badge>
    );
  };

  const handleFormChange = (fieldKey, value) => {
    setForm((prev) => {
      const nextForm = {
        ...prev,
        [fieldKey]: value,
      };

      if (fieldKey === "goktasCikisKg" || fieldKey === "mosCikisKg") {
        const goktasKg =
          fieldKey === "goktasCikisKg"
            ? Number(value) || 0
            : Number(prev.goktasCikisKg) || 0;

        const mosKg =
          fieldKey === "mosCikisKg"
            ? Number(value) || 0
            : Number(prev.mosCikisKg) || 0;

        nextForm.farkKg = goktasKg - mosKg;
      }

      if (fieldKey === "aracGirisSaat" || fieldKey === "aracCikisSaat") {
        const girisSaat =
          fieldKey === "aracGirisSaat" ? value : prev.aracGirisSaat;

        const cikisSaat =
          fieldKey === "aracCikisSaat" ? value : prev.aracCikisSaat;

        nextForm.sureFark = calculateDuration(girisSaat, cikisSaat);
      }

      return nextForm;
    });
  };

  const formFields = [
    { label: "Sıra", key: "sira", type: "number", col: 2 },
    { label: "Tarih", key: "tarih", type: "date", col: 3 },
    { label: "İrsaliye No", key: "irsaliyeNo", type: "text", col: 3 },
    { label: "Plaka", key: "plaka", type: "text", col: 3 },
    { label: "Müşteri", key: "musteri", type: "text", col: 4 },
    {
      label: "Sevk Yeri",
      defaultValue: "GÖKTAŞ",
      key: "sevkYeri",
      type: "text",
      col: 3,
    },
    { label: "Cinsi", key: "cinsi", type: "text", col: 3 },
    {
      label: "Tip",
      key: "tip",
      type: "select",
      options: ["SOĞUK", "GALVANİZ", "SICAK", "HURDA SAC"],
      col: 3,
    },
    {
      label: "Durum",
      key: "durum",
      type: "select",
      options: [
        "SEVK",
        "FASON DİLME",
        "Y.İÇİ SATIN ALMA",
        "İADE",
        "BEKLEMEDE",
        "İHRACAT",
        "İTHALAT",
      ],
      col: 3,
    },
    { label: "Adet", key: "adet", type: "number", col: 3 },
    { label: "Firma Giriş KG", key: "firmaGirisKg", type: "number", col: 3 },
    { label: "Göktaş Çıkış KG", key: "goktasCikisKg", type: "number", col: 3 },
    { label: "Satış Türü", key: "satisTur", type: "text", col: 3 },
    { label: "MOS Çıkış KG", key: "mosCikisKg", type: "number", col: 3 },
    { label: "Fark KG", key: "farkKg", type: "number", col: 3 },
    { label: "Nakliye", key: "nakliye", type: "text", col: 3 },
    { label: "Fatura No", key: "faturaNo", type: "text", col: 3 },
    { label: "Araç Giriş", key: "aracGirisSaat", type: "time", col: 3 },
    { label: "Araç Çıkış", key: "aracCikisSaat", type: "time", col: 3 },
    { label: "Süre Fark", key: "sureFark", type: "text", col: 3 },
    { label: "Konteyner No", key: "konteynerNo", type: "text", col: 4 },
  ];

  const selectedPasteRows = pasteRows.filter((row) => row._selected);

  const invalidSelectedPasteRows = selectedPasteRows.filter(
    (row) => row._errors?.length
  );

  return (
    <Layout>
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div>
          <h4 className="fw-bold text-primary m-0">Sevkiyat Yönetimi</h4>
          <small className="text-muted">
            Toplam {totalRecords} kayıt bulundu.
          </small>
        </div>

        <div className="d-flex gap-2 align-items-end bg-white p-2 rounded shadow-sm border">
          <div>
            <label className="small text-muted d-block fw-bold">
              Başlangıç
            </label>

            <Form.Control
              type="date"
              size="sm"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange({ ...dateRange, start: e.target.value })
              }
            />
          </div>

          <div>
            <label className="small text-muted d-block fw-bold">Bitiş</label>

            <Form.Control
              type="date"
              size="sm"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange({ ...dateRange, end: e.target.value })
              }
            />
          </div>

          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              setDateRange({ start: "", end: "" });
              setCurrentPage(1);
            }}
            title="Temizle"
          >
            ↺
          </Button>

          <Button
            size="sm"
            variant="outline-success"
            onClick={handleExportExcel}
            disabled={isExporting}
            title="Excel'e Aktar"
          >
            {isExporting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                İndiriliyor...
              </>
            ) : (
              "Excel'e Aktar"
            )}
          </Button>

          <div className="vr mx-2"></div>

          {permissions?.includes("sevkiyat_add_list") && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  handleClearPasteModal();
                  setShowPasteModal(true);
                }}
              >
                Excel'den Yapıştır
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={() => {
                  setForm({
                    sevkYeri: "GÖKTAŞ",
                    farkKg: 0,
                    sureFark: "",
                  });
                  setEditRow(null);
                  setShowModal(true);
                }}
              >
                + Yeni Ekle
              </Button>
            </>
          )}
        </div>
      </div>

      {permissions?.includes("sevkiyat_live_camera_view") && (
        <ShipmentLiveCameras />
      )}

      {/* TABLE */}
      <Card className="shadow-sm border-0 mb-4">
        <div className="table-responsive">
          {isLoading && !showModal && !deleteRow ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Yükleniyor...</p>
            </div>
          ) : (
            <Table
              bordered
              hover
              size="sm"
              className="mb-0 align-middle"
              style={{ fontSize: "12px", whiteSpace: "nowrap", color: "#333" }}
            >
              <thead className="table-light">
                <tr style={{ height: "40px" }}>
                  <th>SIRA</th>
                  <th style={{ width: "70px" }}>TARİH</th>
                  <th>İRSALİYE NO</th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="plaka"
                      title="PLAKA"
                      options={filterOptions.plaka}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="sevkYeri"
                      title="SEVK YERİ"
                      options={filterOptions.sevkYeri}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="musteri"
                      title="MÜŞTERİ"
                      options={filterOptions.musteri}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="cinsi"
                      title="CİNSİ"
                      options={filterOptions.cinsi}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="tip"
                      title="SOĞUK/GALVANİZ"
                      options={filterOptions.tip}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th className="text-end">ADET</th>
                  <th className="text-end fw-bold text-dark">FİRMA GİRİŞ KG</th>
                  <th className="text-end fw-bold text-dark">
                    GÖKTAŞ ÇIKIŞ KG
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="satisTur"
                      title="SATIŞ TÜRÜ"
                      options={filterOptions.satisTur}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th className="text-end fw-bold text-dark">MOS ÇIKIŞ KG</th>
                  <th className="text-end fw-bold text-dark">FARK KG</th>

                  <th className="text-center">
                    <ExcelColumnFilter
                      columnKey="durum"
                      title="DURUM"
                      options={filterOptions.durum}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>
                    <ExcelColumnFilter
                      columnKey="nakliye"
                      title="NAKLİYE"
                      options={filterOptions.nakliye}
                      activeFilters={columnFilters}
                      onApply={handleFilterUpdate}
                    />
                  </th>

                  <th>FATURA NO</th>
                  <th>ARAÇ GİRİŞ</th>
                  <th>ARAÇ ÇIKIŞ</th>
                  <th>SÜRE</th>
                  <th>KONTEYNER NO</th>

                  {permissions?.includes("sevkiyat_update_list") && (
                    <th className="text-center">Düzenle</th>
                  )}

                  {permissions?.includes("sevkiyat_delete_list") && (
                    <th className="text-center">Sil</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {rows.length > 0 ? (
                  rows.map((r) => {
                    const goktasKg = Number(r.goktasCikisKg) || 0;
                    const mosKg = Number(r.mosCikisKg) || 0;

                    const farkKg =
                      r.farkKg !== undefined && r.farkKg !== null
                        ? Number(r.farkKg) || 0
                        : goktasKg - mosKg;

                    return (
                      <tr key={r._id}>
                        <td className="px-2">{r.sira}</td>
                        <td className="px-2">{formatDateTR(r.tarih)}</td>
                        <td className="px-2">{r.irsaliyeNo}</td>
                        <td className="px-2 fw-bold">{r.plaka}</td>

                        <td
                          className="px-2"
                          style={{
                            maxWidth: "150px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={r.sevkYeri}
                        >
                          {r.sevkYeri}
                        </td>

                        <td
                          className="px-2 fw-semibold"
                          style={{
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={r.musteri}
                        >
                          {r.musteri}
                        </td>

                        <td className="px-2">{r.cinsi}</td>
                        <td className="px-2">{r.tip}</td>
                        <td className="text-end px-2">{r.adet}</td>

                        <td className="text-end px-2 fw-bold text-dark">
                          {fmtNum(r.firmaGirisKg)}
                        </td>

                        <td className="text-end px-2 fw-bold text-dark">
                          {fmtNum(goktasKg)}
                        </td>

                        <td className="px-2">{r.satisTur || ""}</td>

                        <td className="text-end px-2 fw-bold text-dark">
                          {fmtNum(mosKg)}
                        </td>

                        <td
                          className={`text-end px-2 fw-bold ${
                            farkKg < 0 ? "text-danger" : "text-success"
                          }`}
                        >
                          {fmtNum(farkKg)}
                        </td>

                        <td className="text-center px-2">
                          {renderBadge(r.durum)}
                        </td>

                        <td className="px-2">{r.nakliye}</td>
                        <td className="px-2">{r.faturaNo}</td>
                        <td className="px-2">{r.aracGirisSaat}</td>
                        <td className="px-2">{r.aracCikisSaat}</td>

                        <td className="px-2 fw-bold text-primary">
                          {r.sureFark ||
                            calculateDuration(r.aracGirisSaat, r.aracCikisSaat)}
                        </td>

                        <td className="px-2">{r.konteynerNo}</td>

                        {permissions?.includes("sevkiyat_update_list") && (
                          <td className="text-center px-2">
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 text-decoration-none"
                              style={{ fontSize: "12px" }}
                              onClick={() => {
                                const goktasKg = Number(r.goktasCikisKg) || 0;
                                const mosKg = Number(r.mosCikisKg) || 0;

                                setEditRow(r);
                                setForm({
                                  ...r,
                                  farkKg:
                                    r.farkKg !== undefined && r.farkKg !== null
                                      ? Number(r.farkKg) || 0
                                      : goktasKg - mosKg,
                                  sureFark:
                                    r.sureFark ||
                                    calculateDuration(
                                      r.aracGirisSaat,
                                      r.aracCikisSaat
                                    ),
                                });
                                setShowModal(true);
                              }}
                            >
                              DÜZENLE
                            </Button>
                          </td>
                        )}

                        {permissions?.includes("sevkiyat_delete_list") && (
                          <td className="text-center px-2">
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 text-decoration-none text-danger"
                              style={{ fontSize: "12px" }}
                              onClick={() => setDeleteRow(r)}
                            >
                              Sil
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="23" className="text-center py-4">
                      Kayıt bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        {!isLoading && rows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border-top">
            <small className="text-muted">
              Toplam <b>{totalRecords}</b> kayıttan{" "}
              <b>
                {(currentPage - 1) * 20 + 1} -{" "}
                {Math.min(currentPage * 20, totalRecords)}
              </b>{" "}
              arası.
            </small>

            <Pagination size="sm" className="m-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />

              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;

                if (
                  totalPages > 10 &&
                  Math.abs(currentPage - pageNum) > 3 &&
                  pageNum !== 1 &&
                  pageNum !== totalPages
                ) {
                  return null;
                }

                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />

              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Card>

      {/* DASHBOARD KARTLARI */}
      <h6 className="fw-bold mb-3 text-secondary">Hızlı Bakış (Sayfa Özeti)</h6>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Stack gap={3}>
            <Card className="border-0 shadow-sm bg-primary text-white p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="opacity-75 small">SAYFA GÖKTAŞ ÇIKIŞ KG</div>
                  <div className="fw-bold fs-4">
                    {fmtNum(stats.toplamKg)}{" "}
                    <span className="fs-6 fw-normal">kg</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-0 shadow-sm bg-success text-white p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="opacity-75 small">SAYFA SEFER</div>
                  <div className="fw-bold fs-4">
                    {stats.count} <span className="fs-6 fw-normal">adet</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-0 shadow-sm bg-light p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-muted small">SAYFA FARK KG</div>
                  <div
                    className={`fw-bold fs-4 ${
                      stats.toplamFarkKg < 0 ? "text-danger" : "text-success"
                    }`}
                  >
                    {fmtNum(stats.toplamFarkKg)}{" "}
                    <span className="fs-6 fw-normal">kg</span>
                  </div>
                </div>
              </div>
            </Card>
          </Stack>
        </Col>

        <Col md={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom fw-bold py-3">
              En Yüksek Tonajlı Müşteriler (Sayfa)
            </Card.Header>

            <Card.Body className="p-3">
              {stats.topMusteriler.length > 0 ? (
                stats.topMusteriler.map(([musteri, kg], idx) => (
                  <div key={idx} className="mb-3">
                    <div
                      className="d-flex justify-content-between mb-1"
                      style={{ fontSize: "12px" }}
                    >
                      <span
                        className="fw-semibold text-truncate"
                        style={{ maxWidth: "250px" }}
                      >
                        {idx + 1}. {musteri}
                      </span>

                      <span>{fmtNum(kg)} kg</span>
                    </div>

                    <ProgressBar
                      now={stats.toplamKg ? (kg / stats.toplamKg) * 100 : 0}
                      variant={idx === 0 ? "primary" : "info"}
                      style={{ height: "6px" }}
                    />
                  </div>
                ))
              ) : (
                <div className="text-muted small">Veri yok.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* EXCEL'DEN YAPIŞTIR MODALI */}
      <Modal
        show={showPasteModal}
        onHide={() => {
          if (!isPasteSaving) setShowPasteModal(false);
        }}
        size="xl"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton={!isPasteSaving} className="bg-light">
          <Modal.Title className="fw-bold">
            Excel'den Toplu Sevkiyat Aktarımı
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          <Alert variant="info" className="small mb-3">
            Excel'de aktarılacak satırları seçip kopyalayın. Bu alana
            yapıştırınca sistem satırları otomatik okuyacak. Dosya yükleme
            yapılmaz.
          </Alert>

          <Card className="border-0 shadow-sm p-3 mb-3">
            <Form.Label className="fw-bold text-muted">
              Excel verisini buraya yapıştırın
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={7}
              value={pasteText}
              disabled={isPasteSaving}
              placeholder="Excel'den seçili satırları kopyalayıp buraya yapıştırın..."
              onPaste={(e) => {
                const text = e.clipboardData.getData("text");

                if (!text) return;

                e.preventDefault();

                setPasteText(text);
                setPasteRows(parseShipmentPasteText(text));
              }}
              onChange={(e) => {
                const value = e.target.value;

                setPasteText(value);

                if (!value.trim()) {
                  setPasteRows([]);
                  return;
                }

                setPasteRows(parseShipmentPasteText(value));
              }}
            />

            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                Okunan satır: <b>{pasteRows.length}</b> | Seçili:{" "}
                <b>{selectedPasteRows.length}</b> | Hatalı seçili:{" "}
                <b
                  className={
                    invalidSelectedPasteRows.length ? "text-danger" : ""
                  }
                >
                  {invalidSelectedPasteRows.length}
                </b>
              </small>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={isPasteSaving}
                  onClick={handleClearPasteModal}
                >
                  Temizle
                </Button>

                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={isPasteSaving || !pasteText.trim()}
                  onClick={() => handlePreparePasteRows()}
                >
                  Veriyi Kontrol Et
                </Button>
              </div>
            </div>
          </Card>

          {pasteRows.length > 0 && (
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                <div className="fw-bold">Önizleme</div>

                <Form.Check
                  type="checkbox"
                  id="select-all-paste-rows"
                  label="Tümünü Seç"
                  checked={
                    pasteRows.length > 0 &&
                    pasteRows.every((row) => row._selected)
                  }
                  disabled={isPasteSaving}
                  onChange={(e) => handleToggleAllPasteRows(e.target.checked)}
                />
              </Card.Header>

              <div className="table-responsive" style={{ maxHeight: "420px" }}>
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0 align-middle"
                  style={{
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <thead className="table-light sticky-top">
                    <tr>
                      <th style={{ width: "45px" }}>Seç</th>
                      <th>Satır</th>
                      <th>Durum</th>
                      <th>Sıra</th>
                      <th>Tarih</th>
                      <th>İrsaliye No</th>
                      <th>Plaka</th>
                      <th>Sevk Yeri</th>
                      <th>Müşteri</th>
                      <th>Cinsi</th>
                      <th>Tip</th>
                      <th className="text-end">Adet</th>
                      <th className="text-end">Firma Giriş KG</th>
                      <th className="text-end">Göktaş Çıkış KG</th>
                      <th>Satış Türü</th>
                      <th className="text-end">MOS Çıkış KG</th>
                      <th className="text-end">Fark KG</th>
                      <th>Sevkiyat Durum</th>
                      <th>Nakliye</th>
                      <th>Fatura No</th>
                      <th>Araç Giriş</th>
                      <th>Araç Çıkış</th>
                      <th>Süre Fark</th>
                      <th>Konteyner No</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pasteRows.map((row) => {
                      const hasError = row._errors?.length > 0;

                      return (
                        <tr
                          key={row._rowNo}
                          className={hasError ? "table-danger" : ""}
                        >
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={row._selected}
                              disabled={isPasteSaving}
                              onChange={() => handleTogglePasteRow(row._rowNo)}
                            />
                          </td>

                          <td>{row._rowNo}</td>

                          <td>
                            {hasError ? (
                              <OverlayTrigger
                                placement="right"
                                overlay={
                                  <Popover>
                                    <Popover.Body>
                                      {row._errors.map((err, idx) => (
                                        <div key={idx} className="small">
                                          - {err}
                                        </div>
                                      ))}
                                    </Popover.Body>
                                  </Popover>
                                }
                              >
                                <Badge bg="danger">Hatalı</Badge>
                              </OverlayTrigger>
                            ) : (
                              <Badge bg="success">Hazır</Badge>
                            )}
                          </td>

                          <td>{row.sira}</td>
                          <td>{row.tarih}</td>
                          <td>{row.irsaliyeNo}</td>
                          <td className="fw-bold">{row.plaka}</td>
                          <td>{row.sevkYeri}</td>
                          <td>{row.musteri}</td>
                          <td>{row.cinsi}</td>
                          <td>{row.tip}</td>
                          <td className="text-end">{row.adet}</td>

                          <td className="text-end">
                            {fmtNum(row.firmaGirisKg)}
                          </td>

                          <td className="text-end fw-bold">
                            {fmtNum(row.goktasCikisKg)}
                          </td>

                          <td>{row.satisTur || ""}</td>

                          <td className="text-end fw-bold">
                            {fmtNum(row.mosCikisKg)}
                          </td>

                          <td
                            className={`text-end fw-bold ${
                              Number(row.farkKg) < 0
                                ? "text-danger"
                                : "text-success"
                            }`}
                          >
                            {fmtNum(row.farkKg)}
                          </td>

                          <td>{row.durum}</td>
                          <td>{row.nakliye}</td>
                          <td>{row.faturaNo}</td>
                          <td>{row.aracGirisSaat}</td>
                          <td>{row.aracCikisSaat}</td>
                          <td>{row.sureFark}</td>
                          <td>{row.konteynerNo}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={isPasteSaving}
            onClick={() => setShowPasteModal(false)}
          >
            Kapat
          </Button>

          <Button
            variant="primary"
            disabled={
              isPasteSaving ||
              selectedPasteRows.length === 0 ||
              invalidSelectedPasteRows.length > 0
            }
            onClick={handleSavePasteRows}
            className="px-4"
          >
            {isPasteSaving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Toplu Aktarılıyor...
              </>
            ) : (
              `Seçili Satırları Aktar (${selectedPasteRows.length})`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* EKLE / DÜZENLE MODALI */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold">
            {editRow ? "Düzenle" : "Yeni Ekle"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          <Card className="border-0 shadow-sm p-4">
            <Form>
              <Row className="g-3">
                {formFields.map((field) => (
                  <Col md={field.col || 3} key={field.key}>
                    <Form.Label
                      className="text-muted fw-bold mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      {field.label}
                    </Form.Label>

                    {field.type === "select" ? (
                      <Form.Select
                        value={form[field.key] || ""}
                        onChange={(e) =>
                          handleFormChange(field.key, e.target.value)
                        }
                      >
                        <option value="">Seçiniz...</option>

                        {field.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type={field.type}
                        placeholder={field.label}
                        readOnly={
                          field.key === "farkKg" || field.key === "sureFark"
                        }
                        value={
                          field.type === "date"
                            ? getFormDate(form[field.key])
                            : form[field.key] ?? field.defaultValue ?? ""
                        }
                        onChange={(e) =>
                          handleFormChange(field.key, e.target.value)
                        }
                      />
                    )}
                  </Col>
                ))}
              </Row>
            </Form>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={isLoading}
          >
            İptal
          </Button>

          <Button
            variant="primary"
            onClick={handleSave}
            className="px-4"
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              "Kaydet"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SİLME ONAY MODALI */}
      <Modal
        show={!!deleteRow}
        onHide={() => setDeleteRow(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title className="fw-bold fs-5">Silme Onayı</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4 text-center">
          <p className="mb-1">
            <strong className="text-danger">{deleteRow?.plaka}</strong> plakalı
            ve <strong>{deleteRow?.musteri}</strong> müşterisine ait sevkiyat
            kaydını silmek istediğinize emin misiniz?
          </p>

          <small className="text-muted">Bu işlem geri alınamaz.</small>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <Button
            variant="secondary"
            onClick={() => setDeleteRow(null)}
            disabled={isLoading}
          >
            İptal
          </Button>

          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isLoading}
            className="px-4"
          >
            {isLoading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              "Evet, Sil"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default SevkiyatListe;
